import { useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Download, FileText, GraduationCap, Landmark, Printer } from "lucide-react";
import { financialService } from "../services/financialService";
import { academicSetupService } from "../services/academicSetupService";
import { usePermissions } from "../hooks/usePermissions";
import { getAll } from "../services/pagination";
import { reportService } from "../services/reportService";
import { Modal } from "../components/Modal";
import { Field } from "../components/FormLayout";
import { ClientRegister, type RegisterColumn } from "../components/register/ClientRegister";
import { AsAtBar, PeriodBar } from "../components/financial/ReportDateBar";
import { quickDays, quickPeriods } from "../components/financial/reportPeriods";
import { coversText, libraryConfig, reportsFor, type LibraryReport, type ReportFormat } from "../components/financial/reportLibrary";
import { learnerConfig, type Learner } from "../components/reports/learnerRegister";
import { formatSchoolDate } from "../components/students/studentDisplay";
import { todayIso } from "../utils/dates";
import { downloadErrorMessage } from "../utils/apiError";

type Option = { id: number; name: string };
const academicRoles = ["admin", "director", "head_teacher", "director_of_studies", "head_of_department", "class_teacher", "teacher"];
const money = (value: string | number | undefined) => `UGX ${Math.abs(Number(value ?? 0)).toLocaleString()}`;
const day = (value: string) => formatSchoolDate(value) || value;

export default function Reports() {
  const { hasRole, hasCapability } = usePermissions();
  const canViewAcademic = hasRole(academicRoles);
  const canViewFinancial = hasCapability("financial.view_financial_info");

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Reports</h1>
        <p className="text-gray-600">Every report your account can produce, with the dates it covers, ready to download.</p>
      </div>
      {!canViewAcademic && !canViewFinancial && <div className="card p-8 text-center text-gray-500">Your account does not currently have access to report data.</div>}
      {canViewFinancial && <FinancialReports />}
      {canViewAcademic && <AcademicReports />}
    </div>
  );
}

function SectionTitle({ icon, title, description }: { icon: ReactNode; title: string; description: string }) {
  return (
    <div className="flex items-start gap-3">
      <span className="mt-0.5 text-primary-600">{icon}</span>
      <div>
        <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
        <p className="text-sm text-gray-500">{description}</p>
      </div>
    </div>
  );
}

function Tile({ label, value, tone = "text-gray-900", loading }: { label: string; value: string | number; tone?: string; loading?: boolean }) {
  return (
    <div className="card">
      <div className="px-4 py-3">
        <div className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</div>
        {loading ? <div className="mt-1 h-7 w-28 animate-pulse rounded bg-gray-100" /> : <div className={`mt-0.5 text-xl font-semibold tabular-nums ${tone}`}>{value}</div>}
      </div>
    </div>
  );
}

// Financial ------------------------------------------------------------------------------

function FinancialReports() {
  const navigate = useNavigate();
  const { hasCapability } = usePermissions();
  const canExport = hasCapability("financial.export_reports");
  const reports = reportsFor(hasCapability("financial.view_audit_logs"));
  const today = todayIso();
  const [start, setStart] = useState(`${today.slice(0, 7)}-01`);
  const [end, setEnd] = useState(today);
  const [asOf, setAsOf] = useState(today);
  const dates = { start, end, asOf };
  const terms = useQuery({ queryKey: ["financial-academic-terms"], queryFn: academicSetupService.getTerms });
  const glance = useQuery({
    queryKey: ["accounting-report", start, end],
    queryFn: () => financialService.getAccountingReport({ start_date: start, end_date: end }),
  });
  const surplus = Number(glance.data?.income_statement.surplus ?? 0);
  const [downloading, setDownloading] = useState("");
  const [downloadError, setDownloadError] = useState("");
  const download = async (report: LibraryReport, format: ReportFormat) => {
    setDownloading(`${report.key}-${format}`);
    setDownloadError("");
    try {
      await report.download(format, dates);
    } catch (failure) {
      setDownloadError(`The ${report.name.toLowerCase()} could not be downloaded. ${await downloadErrorMessage(failure, "The server could not prepare it.")}`);
    } finally {
      setDownloading("");
    }
  };

  const columns: RegisterColumn<LibraryReport>[] = [
    {
      header: "Report",
      cell: (report) => (
        <div className="min-w-[16rem] whitespace-normal py-0.5">
          <div className="font-medium text-gray-900">{report.name}</div>
          <div className="text-xs text-gray-500">{report.description}</div>
        </div>
      ),
    },
    { header: "Area", cell: (report) => report.group, className: "text-gray-600" },
    { header: "Dates used", cell: (report) => coversText(report, dates, day), className: "tabular-nums text-gray-600" },
    {
      header: "Download",
      cell: (report) =>
        canExport ? (
          <div className="flex gap-1.5" onClick={(event) => event.stopPropagation()}>
            {(["csv", "pdf"] as const).map((format) => (
              <button
                key={format}
                type="button"
                className="pager-button"
                disabled={!!downloading}
                onClick={() => void download(report, format)}
                title={`Download the ${report.name.toLowerCase()} as ${format.toUpperCase()}`}
              >
                {format === "csv" ? <FileText className="h-3.5 w-3.5" /> : <Printer className="h-3.5 w-3.5" />}
                {downloading === `${report.key}-${format}` ? "Preparing…" : format.toUpperCase()}
              </button>
            ))}
          </div>
        ) : (
          <span className="text-xs text-gray-400">View only</span>
        ),
    },
  ];

  return (
    <section className="space-y-4">
      <SectionTitle icon={<Landmark className="h-5 w-5" />} title="Financial reports" description="Pick the dates once; every report below uses them. Open a report to read it on screen in Finance." />

      <div className="card">
        <div className="filter-panel m-4 space-y-4">
          <PeriodBar
            start={start}
            end={end}
            onChange={(nextStart, nextEnd) => {
              setStart(nextStart);
              setEnd(nextEnd);
            }}
            periods={quickPeriods(today, terms.data ?? [])}
            hint="Used by reports that cover a stretch of time. The trial balance and statement of financial position are as at its last day."
          />
          <AsAtBar value={asOf} onChange={setAsOf} days={quickDays(today, terms.data ?? [])} hint="Used by the ageing reports and the ledger reconciliations." />
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <Tile label="Income" value={money(glance.data?.income_statement.income)} loading={glance.isLoading} />
        <Tile label="Expenses" value={money(glance.data?.income_statement.expenses)} loading={glance.isLoading} />
        <Tile label={surplus < 0 ? "Deficit" : "Surplus"} value={money(surplus)} tone={surplus < 0 ? "text-red-700" : "text-green-700"} loading={glance.isLoading} />
        <Tile label="Assets" value={money(glance.data?.balance_sheet.assets)} loading={glance.isLoading} />
        <Tile label="Liabilities" value={money(glance.data?.balance_sheet.liabilities)} loading={glance.isLoading} />
      </div>
      {glance.isError && <p className="text-sm text-red-700">The figures for this period could not be loaded. The downloads below still work.</p>}

      {downloadError && (
        <div role="alert" className="flex items-start justify-between gap-3 rounded-md bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <span>{downloadError}</span>
          <button type="button" onClick={() => setDownloadError("")} className="shrink-0 text-xs font-medium underline">Dismiss</button>
        </div>
      )}

      <ClientRegister
        title="Report library"
        description={canExport ? "Download any report as CSV or PDF, or click a row to open it in Finance." : "Your account can read these reports but not download them. Click a row to open one in Finance."}
        rows={reports}
        config={libraryConfig}
        columns={columns}
        rowKey={(report) => report.key}
        onOpen={(report) => navigate(report.href)}
        openLabel={() => "Open"}
        noun={["report", "reports"]}
        exportName={{ file: "report-library", title: "Report library" }}
        emptyText="No reports are available to your account."
        canExport={false}
      />
    </section>
  );
}

// Academic -------------------------------------------------------------------------------

const learnerColumns: RegisterColumn<Learner>[] = [
  { header: "Learner", cell: (learner) => <span className="font-medium text-gray-900">{learner.name}</span>, exportValue: (learner) => learner.name },
  { header: "Class", cell: (learner) => learner.class_name, exportValue: (learner) => learner.class_name },
  { header: "Results", cell: (learner) => learner.subjects, exportValue: (learner) => learner.subjects, align: "right" },
  {
    header: "Average",
    cell: (learner) => <span className={learner.average_percentage < 50 ? "font-semibold text-red-700" : "font-semibold text-gray-900"}>{learner.average_percentage}%</span>,
    exportValue: (learner) => `${learner.average_percentage}%`,
    align: "right",
  },
  { header: "Division", cell: (learner) => learner.division, exportValue: (learner) => learner.division },
];

function AcademicReports() {
  const [academicYear, setAcademicYear] = useState("");
  const [term, setTerm] = useState("");
  const [classId, setClassId] = useState("");
  const filters = { academic_year: academicYear || undefined, term: term || undefined, class_id: classId || undefined };
  const years = useQuery({ queryKey: ["report-years"], queryFn: () => getAll<Option>("/auth/academic-years/") });
  // Every class in the school, not only those the caller teaches: the summary is school-wide.
  const classes = useQuery({ queryKey: ["report-classes"], queryFn: () => reportService.getAcademicReportClasses() });
  // Real term ids; the server filters by id, not by typed-out names.
  const terms = useQuery({ queryKey: ["report-terms"], queryFn: () => reportService.getAcademicReportTerms() });
  const summary = useQuery({ queryKey: ["academic-report-summary", filters], queryFn: () => reportService.getAcademicSummary(filters) });
  const [previewStudent, setPreviewStudent] = useState<{ id: number; name: string } | null>(null);

  return (
    <section className="space-y-4">
      <SectionTitle icon={<GraduationCap className="h-5 w-5" />} title="Academic reports" description="Results and report cards for the year, term and class you choose." />

      <div className="card">
        <div className="filter-panel m-4 grid gap-3 sm:grid-cols-3">
          <Field label="Academic year">
            <select className="input mt-1" value={academicYear} onChange={(event) => setAcademicYear(event.target.value)}>
              <option value="">All years</option>
              {years.data?.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
            </select>
          </Field>
          <Field label="Term">
            <select className="input mt-1" value={term} onChange={(event) => setTerm(event.target.value)}>
              <option value="">All terms</option>
              {terms.data?.map((item) => <option key={item.id} value={item.id}>{item.name}{item.is_current ? " (current)" : ""}</option>)}
            </select>
          </Field>
          <Field label="Class">
            <select className="input mt-1" value={classId} onChange={(event) => setClassId(event.target.value)}>
              <option value="">All classes</option>
              {classes.data?.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
            </select>
          </Field>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <Tile label="Results recorded" value={summary.data?.results_recorded ?? 0} loading={summary.isLoading} />
        <Tile label="Average score" value={`${summary.data?.average_percentage ?? 0}%`} loading={summary.isLoading} />
        <Tile label="Learners reported" value={summary.data?.learners.length ?? 0} loading={summary.isLoading} />
      </div>

      <ClientRegister
        title="Learner performance"
        description="Click a learner to see their report card and download it."
        rows={summary.data?.learners}
        config={learnerConfig}
        columns={learnerColumns}
        rowKey={(learner) => learner.student_id}
        onOpen={(learner) => setPreviewStudent({ id: learner.student_id, name: learner.name })}
        openLabel={() => "Report card"}
        noun={["learner", "learners"]}
        exportName={{ file: `learner-performance-${todayIso()}`, title: "Learner performance" }}
        isLoading={summary.isLoading}
        isError={summary.isError}
        onRetry={() => void summary.refetch()}
        emptyText="No recorded results match these filters."
      />

      {previewStudent && <ReportCardPreview student={previewStudent} filters={filters} onClose={() => setPreviewStudent(null)} />}
    </section>
  );
}

function ReportCardPreview({
  student,
  filters,
  onClose,
}: {
  student: { id: number; name: string };
  filters: { academic_year?: string; term?: string };
  onClose: () => void;
}) {
  const preview = useQuery({
    queryKey: ["report-card-preview", student.id, filters.academic_year, filters.term],
    queryFn: () => reportService.getReportCard(student.id, filters),
  });
  const card = preview.data;
  return (
    <Modal
      isOpen
      size="3xl"
      title={`${student.name}'s report card`}
      onClose={onClose}
      footer={
        <>
          <button type="button" onClick={onClose} className="btn btn-outline">Close</button>
          <button type="button" disabled={!card} onClick={() => void reportService.downloadReportCard(student.id, filters)} className="btn btn-primary">
            <Download className="mr-2 h-4 w-4" />Download PDF
          </button>
        </>
      }
    >
      {preview.isLoading && <p className="py-6 text-center text-sm text-gray-500">Loading the report card…</p>}
      {preview.isError && <p className="rounded-md bg-red-50 p-4 text-sm text-red-700">This report card could not be loaded.</p>}
      {card && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-end justify-between gap-3 rounded-lg bg-primary-50 px-4 py-3">
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-primary-700">
                {[card.student.class_name, card.student.admission_number, card.term, card.academic_year?.name].filter(Boolean).join(" · ")}
              </p>
              <p className="text-2xl font-semibold tabular-nums text-gray-900">{card.summary.average_percentage}% average</p>
            </div>
            <p className="text-sm text-gray-600">{card.summary.overall_division} · passed {card.summary.passes} of {card.summary.subjects}</p>
          </div>
          {card.results.length ? (
            <div className="sheet-wrap">
              <table className="sheet">
                <thead>
                  <tr>
                    <th>Subject</th>
                    <th>Examination</th>
                    <th className="text-right">Marks</th>
                    <th className="text-right">%</th>
                    <th>Division</th>
                    <th>Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  {card.results.map((row) => (
                    <tr key={row.result_id}>
                      <td className="font-medium text-gray-900">{row.subject}</td>
                      <td className="text-gray-600">{row.examination}</td>
                      <td className="text-right tabular-nums">{row.marks}/{row.total_marks}</td>
                      <td className="text-right tabular-nums">{row.percentage}%</td>
                      <td>{row.division}</td>
                      <td className="whitespace-normal text-gray-600">{row.remarks || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="rounded-lg border border-dashed border-gray-300 py-6 text-center text-sm text-gray-500">No results recorded for this selection.</p>
          )}
        </div>
      )}
    </Modal>
  );
}
